/**
 * ImportInventoryTab — bulk stock upload for a pharmacy.
 *
 * Rendered as the third tab of PharmacyInventory.jsx.
 *
 * The pharmacist (or admin) uploads an Excel (.xlsx) inventory-export sheet:
 *
 *     Product | Form | Company | … | MRP (Rs) | … | No of Packs | Loose Units
 *
 * A "Download sample Excel" button hands back the exact template. Rows are stored
 * DIRECTLY as the pharmacy's inventory (no catalogue matching). The file can hold
 * lakhs of rows, so the backend processes it in the BACKGROUND and we poll for
 * progress. The UI mirrors the engine's report — "this many processed, added,
 * updated, duplicates collapsed, and these errored".
 *
 * Endpoints (mounted at /pharmacy):
 *   POST /pharmacy/import/:pharmacyId          start (multipart "file") → { jobId }
 *   GET  /pharmacy/import/status/:jobId        live progress + final report
 *   GET  /pharmacy/import/history/:pharmacyId  past imports
 *   GET  /pharmacy/import/sample-template      ready-to-fill .xlsx template
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  LinearProgress,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
  Divider,
  Tooltip,
  IconButton,
  Collapse,
} from "@mui/material";
import {
  MdCloudUpload,
  MdUploadFile,
  MdCheckCircle,
  MdError,
  MdInsertDriveFile,
  MdRefresh,
  MdWarningAmber,
  MdContentCopy,
  MdHistory,
  MdExpandMore,
  MdExpandLess,
  MdDownload,
} from "react-icons/md";

const BASE_URL = "https://api.medstown.com";
const SAMPLE_TEMPLATE_URL = `${BASE_URL}/pharmacy/import/sample-template`;
const TEAL = "#004D4D";

// Columns the importer also reads when present (the four below are the ones it
// keys on). Shown as a  hint under the upload card; the full set ships in the
// downloadable template at SAMPLE_TEMPLATE_URL.
const EXTRA_COLUMNS =
  "Form, Barcode, Rx Type, Category, Batch, HSN Code, Expiry, Net Rate (Rs), Sale GST %, Loose Units";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
const prettyBytes = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};
const prettyDate = (d) => (d ? new Date(d).toLocaleString("en-IN") : "—");

// One result metric tile. `tone` picks the accent colour.
function StatTile({ label, value, tone = TEAL, icon }) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, borderRadius: 2, textAlign: "center", minWidth: 104, flex: 1 }}
    >
      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
        {icon}
        <Typography variant="h6" fontWeight={800} sx={{ color: tone, lineHeight: 1 }}>
          {fmt(value)}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
        {label}
      </Typography>
    </Paper>
  );
}

const STATUS_CHIP = {
  queued: { label: "Queued", color: "default" },
  processing: { label: "Processing", color: "warning" },
  completed: { label: "Completed", color: "success" },
  failed: { label: "Failed", color: "error" },
};

export default function ImportInventoryTab({ pharmacyId, onImported }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [job, setJob] = useState(null); // live status doc from /status
  const [dragOver, setDragOver] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, []);

  // ── history ────────────────────────────────────────────────────────────────
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/pharmacy/import/history/${pharmacyId}?limit=10`);
      if (!mountedRef.current) return;
      setHistory(res.data?.data?.list || []);
    } catch (e) {
      /* non-fatal */
    } finally {
      if (mountedRef.current) setHistoryLoading(false);
    }
  }, [pharmacyId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // ── polling ──────────────────────────────────────────────────────────────--
  const poll = useCallback(
    async (jobId) => {
      try {
        const res = await axios.get(`${BASE_URL}/pharmacy/import/status/${jobId}`);
        if (!mountedRef.current) return;
        const data = res.data?.data;
        setJob(data);
        if (data.status === "completed" || data.status === "failed") {
          if (data.status === "completed") {
            toast.success(
              `Import done — ${fmt(data.addedCount)} added, ${fmt(data.updatedCount)} updated`
            );
            onImported?.();
          } else {
            toast.error(data.failureReason || "Import failed");
          }
          loadHistory();
          return; // stop polling
        }
        pollRef.current = setTimeout(() => poll(jobId), 1500);
      } catch (e) {
        if (mountedRef.current) {
          toast.error("Lost connection to the import job");
        }
      }
    },
    [loadHistory, onImported]
  );

  // ── file selection ───────────────────────────────────────────────────────--
  const pickFile = (f) => {
    if (!f) return;
    if (!/\.xlsx$/i.test(f.name)) {
      toast.error("Only Excel .xlsx files are supported");
      return;
    }
    setFile(f);
    setJob(null);
    setShowErrors(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    pickFile(e.dataTransfer.files?.[0]);
  };

  // ── upload ─────────────────────────────────────────────────────────────────
  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setJob(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${BASE_URL}/pharmacy/import/${pharmacyId}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const jobId = res.data?.data?.jobId;
      if (!jobId) throw new Error("No job id returned");
      setJob({ jobId, status: "queued", progress: 0 });
      poll(jobId);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Upload failed");
    } finally {
      if (mountedRef.current) setUploading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setJob(null);
    setShowErrors(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const running = job && (job.status === "queued" || job.status === "processing");
  const done = job && (job.status === "completed" || job.status === "failed");

  return (
    <Box>
      {/* ── Upload card ──────────────────────────────────────────────────── */}
      {!job && (
        <Paper
          variant="outlined"
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          sx={{
            borderRadius: 3,
            borderStyle: "dashed",
            borderWidth: 2,
            borderColor: dragOver ? TEAL : "#cbd5e1",
            backgroundColor: dragOver ? "rgba(0,77,77,0.04)" : "#fafafa",
            p: { xs: 3, md: 5 },
            textAlign: "center",
            transition: "all .15s",
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            hidden
            onChange={(e) => pickFile(e.target.files?.[0])}
          />
          <MdCloudUpload size={48} color={TEAL} />
          <Typography variant="h6" fontWeight={800} sx={{ mt: 1 }}>
            Upload current stock
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Drag &amp; drop your Excel (.xlsx) file here, or browse. Every row is added
            straight to this pharmacy's inventory.
          </Typography>

          {file ? (
            <Stack spacing={1.5} alignItems="center">
              <Chip
                icon={<MdInsertDriveFile />}
                label={`${file.name}  ·  ${prettyBytes(file.size)}`}
                onDelete={reset}
                variant="outlined"
                sx={{ maxWidth: "100%", "& .MuiChip-label": { fontWeight: 600 } }}
              />
              <Button
                variant="contained"
                size="large"
                startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <MdUploadFile />}
                disabled={uploading}
                onClick={upload}
                sx={{ backgroundColor: TEAL, "&:hover": { backgroundColor: "#003838" }, px: 4 }}
              >
                {uploading ? "Starting…" : "Start import"}
              </Button>
            </Stack>
          ) : (
            <Button
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              sx={{ color: TEAL, borderColor: TEAL, "&:hover": { borderColor: "#003838" } }}
            >
              Browse files
            </Button>
          )}

          <Divider sx={{ my: 2.5 }} />

          <Button
            variant="outlined"
            startIcon={<MdDownload />}
            href={SAMPLE_TEMPLATE_URL}
            target="_blank"
            rel="noopener"
            sx={{ color: TEAL, borderColor: TEAL, "&:hover": { borderColor: "#003838" }, mb: 1.5 }}
          >
            Download sample Excel
          </Button>

          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            Header row should include:&nbsp;
            <b>Product</b>, <b>Company</b>, <b>MRP (Rs)</b>, <b>No of Packs</b> (any order). Stock
            is <b>No of Packs</b> (+ Loose Units) — a plain number. Duplicate rows are collapsed
            automatically.
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.5 }}>
            Also read when present: {EXTRA_COLUMNS}. Any other columns are ignored.
          </Typography>
        </Paper>
      )}

      {/* ── Progress / result ────────────────────────────────────────────── */}
      {job && (
        <Paper variant="outlined" sx={{ borderRadius: 3, p: { xs: 2, md: 3 } }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            {job.status === "completed" ? (
              <MdCheckCircle size={26} color="#16a34a" />
            ) : job.status === "failed" ? (
              <MdError size={26} color="#dc2626" />
            ) : (
              <CircularProgress size={22} sx={{ color: TEAL }} />
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={800}>
                {job.status === "completed"
                  ? "Import complete"
                  : job.status === "failed"
                  ? "Import failed"
                  : "Importing…"}
              </Typography>
              {job.fileName && (
                <Typography variant="caption" color="text.secondary">
                  {job.fileName}
                </Typography>
              )}
            </Box>
            <Chip
              size="small"
              label={STATUS_CHIP[job.status]?.label || job.status}
              color={STATUS_CHIP[job.status]?.color || "default"}
            />
          </Stack>

          {/* progress bar */}
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {fmt(job.processedRows)} / {fmt(job.totalRows)} rows processed
              </Typography>
              <Typography variant="caption" fontWeight={700} sx={{ color: TEAL }}>
                {job.progress ?? 0}%
              </Typography>
            </Stack>
            <LinearProgress
              variant={running && !job.totalRows ? "indeterminate" : "determinate"}
              value={job.progress ?? 0}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: "#e2e8f0",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: job.status === "failed" ? "#dc2626" : TEAL,
                  borderRadius: 5,
                },
              }}
            />
          </Box>

          {job.status === "failed" && job.failureReason && (
            <Paper
              variant="outlined"
              sx={{ p: 1.5, mb: 2, borderColor: "#fecaca", backgroundColor: "#fef2f2", borderRadius: 2 }}
            >
              <Typography variant="body2" color="error">
                {job.failureReason}
              </Typography>
            </Paper>
          )}

          {/* metric tiles */}
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.5} sx={{ mb: 1 }}>
            <StatTile label="Added" value={job.addedCount} tone="#16a34a" icon={<MdCheckCircle color="#16a34a" size={18} />} />
            <StatTile label="Updated" value={job.updatedCount} tone="#2563eb" icon={<MdRefresh color="#2563eb" size={18} />} />
            <StatTile label="Duplicates" value={job.duplicateCount} tone="#64748b" icon={<MdContentCopy color="#64748b" size={18} />} />
            <StatTile label="Errors" value={job.errorCount} tone="#dc2626" icon={<MdWarningAmber color="#dc2626" size={18} />} />
          </Stack>

          {/* error details */}
          {job.errors?.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Button
                size="small"
                onClick={() => setShowErrors((s) => !s)}
                startIcon={showErrors ? <MdExpandLess /> : <MdExpandMore />}
                sx={{ color: "#dc2626", textTransform: "none", fontWeight: 700 }}
              >
                {showErrors ? "Hide" : "Show"} {fmt(job.errorCount)} problem row(s)
              </Button>
              <Collapse in={showErrors}>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mt: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#fef2f2" }}>
                        <TableCell sx={{ width: 80 }}>Row</TableCell>
                        <TableCell>Product</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {job.errors.map((e, i) => (
                        <TableRow key={i}>
                          <TableCell>{e.row}</TableCell>
                          <TableCell>{e.name || "—"}</TableCell>
                          <TableCell>
                            <Typography variant="caption" color="error">
                              {e.reason}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {job.errorsTruncated && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    Showing the first {job.errors.length} problems only.
                  </Typography>
                )}
              </Collapse>
            </Box>
          )}

          {done && (
            <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
              <Button
                variant="contained"
                onClick={reset}
                startIcon={<MdUploadFile />}
                sx={{ backgroundColor: TEAL, "&:hover": { backgroundColor: "#003838" } }}
              >
                Import another file
              </Button>
            </Stack>
          )}
        </Paper>
      )}

      {/* ── History ───────────────────────────────────────────────────────── */}
      <Box sx={{ mt: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <MdHistory color="#64748b" />
          <Typography variant="subtitle2" fontWeight={800} color="text.secondary">
            Recent imports
          </Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={loadHistory}>
              <MdRefresh />
            </IconButton>
          </Tooltip>
        </Stack>

        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                <TableCell>File</TableCell>
                <TableCell>When</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Rows</TableCell>
                <TableCell align="right">Added</TableCell>
                <TableCell align="right">Updated</TableCell>
                <TableCell align="right">Errors</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historyLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={20} sx={{ color: TEAL }} />
                  </TableCell>
                </TableRow>
              ) : history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No imports yet
                  </TableCell>
                </TableRow>
              ) : (
                history.map((h) => (
                  <TableRow key={h._id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <MdInsertDriveFile color="#94a3b8" />
                        <Typography variant="body2" sx={{ maxWidth: 220 }} noWrap>
                          {h.fileName || "upload"}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {prettyDate(h.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={STATUS_CHIP[h.status]?.label || h.status}
                        color={STATUS_CHIP[h.status]?.color || "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">{fmt(h.totalRows)}</TableCell>
                    <TableCell align="right" sx={{ color: "#16a34a", fontWeight: 700 }}>
                      {fmt(h.addedCount)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: "#2563eb", fontWeight: 700 }}>
                      {fmt(h.updatedCount)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: h.errorCount ? "#dc2626" : "inherit" }}>
                      {fmt(h.errorCount)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
